import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { WalletButton } from "@/components/WalletButton";
import { useLang } from "@/context/LanguageContext";

const LINKS = [
  { to: "/explore", key: "explore", testid: "nav-explore" },
  { to: "/lore", key: "lore", testid: "nav-lore" },
  { to: "/marketplace", key: "marketplace", testid: "nav-marketplace" },
  { to: "/hall", key: "hall", testid: "nav-hall" },
  { to: "/profile", key: "profile", testid: "nav-profile" },
];

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <button
      data-testid="lang-toggle"
      onClick={() => setLang(lang === "en" ? "it" : "en")}
      title="Language"
      className="flex items-center gap-1.5 px-2.5 py-2 border border-cosmic/20 text-cosmic hover:text-portal hover:border-portal/50 transition-colors font-mono text-[0.7rem] tracking-widest"
    >
      <Globe size={13} /> {lang.toUpperCase()}
    </button>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { t } = useLang();
  if (loc.pathname.startsWith("/admin")) return null;

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass">
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/textures/logo.png" alt="The Portal Universe" className="h-9 w-9 object-contain" />
          <div className="leading-none">
            <div className="font-display text-sm tracking-[0.25em] text-white">THE PORTAL</div>
            <div className="text-overline text-[0.6rem] mt-0.5">{t.nav.mission}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `font-mono text-[0.72rem] tracking-[0.18em] uppercase transition-colors ${
                  isActive ? "text-portal" : "text-cosmic/70 hover:text-white"
                }`
              }
            >
              {t.nav[l.key]}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangToggle />
          <WalletButton />
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LangToggle />
          <button
            data-testid="mobile-menu-toggle"
            className="text-cosmic"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-cosmic/10 px-5 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="font-mono text-sm tracking-[0.18em] uppercase text-cosmic/80"
            >
              {t.nav[l.key]}
            </NavLink>
          ))}
          <div className="pt-2"><WalletButton /></div>
        </div>
      )}
    </header>
  );
}
